/* The scenario library, in the Deck.
 *
 * Two things live here and they are deliberately different weights:
 *
 *   * **Run** — switch the exercise. EXCON only, and it replaces the world for everyone
 *     connected, so it confirms first and says what it will do.
 *   * **Open the ORBAT builder** — a link out. The Deck does not author scenarios; a second
 *     editor would be a second idea of what a valid scenario is.
 *
 * The Run button is DISABLED with its reason rather than hidden for a non-EXCON role. A control
 * that vanishes teaches nobody why it was not theirs to press, and the next person re-implements
 * it. Same rule the clock controls already follow.
 */
import { useEffect, useState } from 'react'
import { ExternalLink, Play, RefreshCw, X } from 'lucide-react'
import { useDeck } from '../state/store'
import { activateScenario, fetchLibrary, type LibraryScenario } from '../lib/scenarios'

export function ScenarioPanel() {
  const open = useDeck((s) => s.scenarioOpen)
  const toggle = useDeck((s) => s.toggleScenario)
  const me = useDeck((s) => s.me)
  const [items, setItems] = useState<LibraryScenario[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')

  const load = async () => {
    try {
      const lib = await fetchLibrary()
      setItems(lib.scenarios)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const run = async () => {
      try {
        const lib = await fetchLibrary()
        if (!cancelled) {
          setItems(lib.scenarios)
          setError('')
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      }
    }
    void run()
    // Polled slowly. The library changes when somebody saves in the builder — a minute of
    // staleness costs nothing, and this is not part of the picture.
    const t = window.setInterval(run, 5000)
    return () => {
      cancelled = true
      window.clearInterval(t)
    }
  }, [open])

  if (!open) return null

  const mayRun = me?.role === 'umpire'

  const onRun = async (s: LibraryScenario) => {
    const ok = window.confirm(
      `Run “${s.name}” — ${s.units} units?\n\n` +
        'This replaces the running exercise for everyone connected and restarts at tick 0, paused.',
    )
    if (!ok) return
    setBusy(s.slug)
    try {
      await activateScenario(s.slug)
      setError('')
      // The snapshot's scenario digest changes on the next frame, and the store clears every
      // per-entity thing the old world owned. Nothing to do here but refresh the list.
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy('')
    }
  }

  return (
    <div
      className="panel absolute left-3 top-3 z-20 flex w-[27rem] flex-col overflow-hidden"
      style={{ maxHeight: 'calc(100% - 24px)' }}
    >
      <div
        className="flex shrink-0 items-center gap-2 px-3 py-2"
        style={{ borderBottom: '1px solid var(--color-line-2)' }}
      >
        <span className="title">Scenario library</span>
        <button
          className="ctl ml-auto"
          style={{ height: 20, padding: '0 6px', fontSize: 10 }}
          onClick={() => void load()}
          aria-label="Refresh the scenario library"
          title="Refresh"
        >
          <RefreshCw size={11} />
        </button>
        <a
          className="ctl"
          href="/scenario/"
          target="_blank"
          rel="noopener"
          style={{ height: 20, padding: '0 6px', fontSize: 10, display: 'inline-flex', gap: 4, alignItems: 'center' }}
          title="Open the ORBAT builder in a new tab"
        >
          <ExternalLink size={11} /> Builder
        </a>
        <button
          className="ctl"
          style={{ width: 20, height: 20, padding: 0 }}
          onClick={toggle}
          aria-label="Close the scenario library panel"
        >
          <X size={12} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error && (
          <p className="px-3 py-2" style={{ fontSize: 10.5, color: 'var(--color-crit)' }}>
            {error}
          </p>
        )}
        {!error && items.length === 0 && (
          <p className="px-3 py-3" style={{ fontSize: 10.5, color: 'var(--color-text-3)' }}>
            The library is empty. Author one in the ORBAT builder and save it.
          </p>
        )}

        {items.map((s) => (
          <div
            key={s.slug}
            className="px-3 py-2"
            style={{
              borderBottom: '1px solid var(--color-line-1)',
              background: s.active ? 'var(--color-accent-dim)' : undefined,
            }}
          >
            <div className="flex items-baseline gap-2">
              <span className="num" style={{ fontSize: 11.5, color: 'var(--color-text-1)' }}>
                {s.name}
              </span>
              {s.active && (
                <span className="label" style={{ fontSize: 8, color: 'var(--color-accent)' }}>
                  RUNNING
                </span>
              )}
              {!s.runnable && (
                <span className="label" style={{ fontSize: 8, color: 'var(--color-crit)' }}>
                  NOT RUNNABLE
                </span>
              )}
              <button
                className="ctl ml-auto"
                style={{ height: 22, padding: '0 8px', fontSize: 10, display: 'inline-flex', gap: 4, alignItems: 'center' }}
                disabled={!mayRun || !s.runnable || busy === s.slug}
                onClick={() => void onRun(s)}
                title={
                  !s.runnable
                    ? 'This scenario does not validate against the loaded dataset'
                    : mayRun
                      ? 'Replaces the running exercise for everyone, at tick 0, paused'
                      : 'Only EXCON may change the running exercise'
                }
              >
                <Play size={10} /> {s.active ? 'Restart' : 'Run'}
              </button>
            </div>
            <div className="num" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
              {s.units} units · {s.sides.join(' / ').toUpperCase()} · {s.classification} ·{' '}
              {s.digest.slice(0, 12)}
            </div>
            {!s.runnable && (
              <div style={{ fontSize: 9.5, color: 'var(--color-crit)' }}>{s.problems.join('; ')}</div>
            )}
          </div>
        ))}
      </div>

      <p
        className="shrink-0 px-3 py-1.5"
        style={{ fontSize: 9.5, color: 'var(--color-text-3)', borderTop: '1px solid var(--color-line-2)' }}
      >
        Running a scenario restarts the exercise at tick 0, paused. Plotted courses, the selection
        and mission assignments are cleared, because they name units the new world may not have.
      </p>
    </div>
  )
}
